import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Paper, Box, Typography, Button, IconButton, Divider, CircularProgress, Backdrop } from '@mui/material';
import ShareIcon from '@mui/icons-material/Share';
import ChecklistIcon from '@mui/icons-material/Checklist';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import axios from 'axios';
import { API_BASE_URL } from '../../../lib/constants';

import { useConsiderationPhase } from './hooks/useConsiderationPhase';
import BasicInfoSection from './screens/BasicInfoSection';
import MedicalHistorySection from './screens/MedicalHistorySection';
import InstructionsSection from './screens/InstructionsSection';
import RisksBenefitsSection from './screens/RisksBenefitsSection';
import TestsSection from './screens/TestsSection';
import SurgeryDetailsSection from './screens/SurgeryDetailsSection';
import ConsentSection from './screens/ConsentSection';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

const ConsiderationPhase = () => {
    const { uhid_no } = useParams();
    const navigate = useNavigate();
    
    const {
      formData, setFormData, errors, loading, success, patient,
      handleChange, handleSubmit
    } = useConsiderationPhase(uhid_no, navigate);

    const handleShare = async () => {
      try {
        const res = await axios.post(`${API_BASE_URL}/generate_for_consideration_api_token/${uhid_no}`);
        const link = `${API_BASE_URL}/considerationPhase/${uhid_no}?token=${res.data.token}`;
        await navigator.clipboard.writeText(link);
        const message = `Dear Doctor,\n\nPlease review/fill the Consideration Phase Form for UHID: ${uhid_no}\nLink: ${link}`;
        window.open(`http://wa.me/+916379605575?text=${encodeURIComponent(message)}`, '_blank');
      } catch (err) {
        console.error("Sharing failed", err);
      }
    };

    if (loading && !patient.name) {
      return (
        <Backdrop open={true} sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}>
          <CircularProgress color="inherit" />
        </Backdrop>
      );
    }

    return (
      <ThemeProvider theme={theme}>
        <Container maxWidth="lg" sx={{ my: 4 }}>
          <Paper elevation={4} sx={{ p: 4, borderRadius: 3, borderTop: '8px solid #008080' }}>
            
            {/* Header Section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <ChecklistIcon sx={{ color: '#008080', fontSize: 40 }} />
                    <Typography variant="h4" sx={{ color: '#008080', fontWeight: 'bold', letterSpacing: 1 }}>
                        CONSIDERATION PHASE
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <img src="/logo.png" alt="Logo" style={{ height: 40 }} />
                    <Typography variant="h6" sx={{ color: '#008080', fontWeight: 'bold' }}>OTMS</Typography>
                </Box>
            </Box>

            {/* Action Bar */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, p: 2, bgcolor: '#f0f4f4', borderRadius: 2 }}>
                <Button variant="outlined" onClick={() => navigate(-1)} sx={{ borderColor: '#008080', color: '#008080' }}>Back</Button>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button variant="contained" onClick={() => navigate('/home')} sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' } }}>Home</Button>
                    <IconButton onClick={handleShare} sx={{ color: '#008080' }}><ShareIcon /></IconButton>
                </Box>
            </Box>

            <Divider sx={{ my: 4 }} />

            {/* Form Content */}
            <BasicInfoSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} patient={patient} />
            <Divider sx={{ my: 4 }} />
            
            <MedicalHistorySection formData={formData} setFormData={setFormData} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <InstructionsSection formData={formData} handleChange={handleChange} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <RisksBenefitsSection formData={formData} setFormData={setFormData} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <TestsSection formData={formData} handleChange={handleChange} />
            <Divider sx={{ my: 4 }} />
            
            <SurgeryDetailsSection formData={formData} handleChange={handleChange} setFormData={setFormData} errors={errors} />
            <Divider sx={{ my: 4 }} />
            
            <ConsentSection formData={formData} handleChange={handleChange} errors={errors} />

            {/* Form Footer */}
            <Box sx={{ mt: 6, p: 3, bgcolor: '#f0f4f4', borderRadius: 3, display: 'flex', gap: 2 }}>
                <DraftSaveButton
                    formKey={DRAFT_KEYS.CONSIDERATION_PHASE}
                    uhidNo={uhid_no}
                    formData={formData}
                    onDraftLoaded={(draft) => setFormData(prev => ({ ...prev, ...draft }))}
                    isUpdate={success}
                    fullWidth
                    sx={{ height: 56 }}
                />
                <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSubmit}
                    sx={{ bgcolor: '#008080', '&:hover': { bgcolor: '#006666' }, height: 56, fontWeight: 'bold' }}
                >
                    {success ? 'Update Consideration Phase' : 'Submit Consideration Phase'}
                </Button>
            </Box>

          </Paper>
        </Container>
      </ThemeProvider>
    );
};

export default ConsiderationPhase;
